/**
 * Chart series recipe: the chip recipe from app/lib/chipColors.ts carried onto
 * charts. A series is the brand tag hue at low alpha for the fill and the same
 * tag hue pulled darker for the stroke, so the outline is a deeper shade of the
 * mark itself, like a badge's border.
 *
 * The hues are the `--color-tags-*` values from tailwind.css. They are literals
 * here because Tailwind v4 only emits theme variables that are referenced as
 * variables, and the chip utilities inline theirs.
 */
import type { ChartHue, SeriesStyle } from 'app/types/chartColors'

const tag = {
  gray: `87.17% 0.0093 258.34`,
  silver: `92.76% 0.0058 264.53`,
  salmon: `71.06% 0.1661 22.22`,
  orange: `75.76% 0.159 55.93`,
  yellow: `90.52% 0.1657 98.11`,
  green: `77.29% 0.1535 149`,
  blue: `71.37% 0.1434 254.62`,
  purple: `72.17% 0.1767 305.5`,
  pink: `72.53% 0.1752 349.76`,
  amber: `83.69% 0.1644 84.43`,
  sky: `71% 0.13 225`,
}

const grayInk = `40% 0.012 258`

/**
 * The color at `alpha` composited over the current surface, as an opaque color: the
 * same look as a translucent chip tint on a card, without hover greys or neighbouring
 * fills bleeding through. Mixed in sRGB to match alpha compositing. Matting onto
 * `--surface` (rather than a literal white) keeps the composite correct on the dark
 * surface too, so a fill still reads as a tint of the card instead of a muddy patch.
 */
const matte = (color: string, alpha: number) =>
  `color-mix(in srgb, ${color} ${Math.round(alpha * 100)}%, var(--surface))`

const OUTLINE_DARKEN = 0.12

/** `ink`/`outline` replace the computed colors with CSS values, for a hue that must change with the theme. */
type HueSource = { tint: string; rest: number; hover: number; darken: number; ink?: string; outline?: string }

const hue = (tint: string, rest = 0.47, hover = 0.68, darken = OUTLINE_DARKEN): HueSource => ({
  tint,
  rest,
  hover,
  darken,
})

// The light hues need higher rest/hover alphas to read at all. Graphite is the
// gray ink as its own tint, a clear step darker than gray. Gray and silver are the palest
// fills, so their outlines darken further to hold an edge as firm as the colored hues.
const sources: Record<ChartHue, HueSource> = {
  gray: hue(tag.gray, 0.67, 0.9, 0.22),
  silver: hue(tag.silver, 0.85, 1, 0.26),
  // Graphite is a dark ink, so on the dark surface it vanished (outline about 1.3:1). Its colors come
  // from --chart-graphite(-outline), which keep these exact light values and lift in .dark.
  graphite: { ...hue(grayInk, 0.58, 0.75), ink: `var(--chart-graphite)`, outline: `var(--chart-graphite-outline)` },
  red: hue(tag.salmon),
  orange: hue(tag.orange),
  amber: hue(tag.amber, 0.4, 0.6),
  yellow: hue(tag.yellow, 0.44, 0.64),
  green: hue(tag.green),
  sky: hue(tag.sky),
  blue: hue(tag.blue),
  // A dark ink, themed through CSS for the same reason as graphite.
  navy: { ...hue(`42.4% 0.181 265.6`, 0.58, 0.75), ink: `var(--chart-navy)`, outline: `var(--chart-navy-outline)` },
  purple: hue(tag.purple),
  pink: hue(tag.pink),
}

export function hueTint(name: ChartHue, alpha: number): string {
  return matte(sources[name].ink ?? `oklch(${sources[name].tint})`, alpha)
}

export function hueOutline(name: ChartHue): string {
  const { outline } = sources[name]
  if (outline) return outline
  // Lower lightness only: keeping chroma and hue stops the outline drifting toward grey.
  const [l, c, h] = sources[name].tint.split(` `)
  return `oklch(${(parseFloat(l) - sources[name].darken * 100).toFixed(2)}% ${c} ${h})`
}

export const chartHueStyles: Readonly<Record<ChartHue, SeriesStyle>> = Object.fromEntries(
  (Object.keys(sources) as ChartHue[]).map((name) => [
    name,
    {
      fill: hueTint(name, sources[name].rest),
      fillHover: hueTint(name, sources[name].hover),
      stroke: hueOutline(name),
    },
  ]),
) as Record<ChartHue, SeriesStyle>

export const CHANNEL_HUES: Readonly<Record<string, ChartHue>> = {
  Unknown: `silver`,
  'Organic Search': `green`,
  'Paid Search': `blue`,
  'Organic Social': `purple`,
  'Paid Social': `sky`,
  Email: `red`,
  Referral: `orange`,
  Display: `pink`,
  'Paid Video': `amber`,
  Direct: `graphite`,
  Offline: `gray`,
  Print: `navy`,
}

/** The coarse channel groups the SMC org reports on instead. */
export const SMC_CHANNEL_GROUP_HUES: Readonly<Record<string, ChartHue>> = {
  'Digital Marketing': `blue`,
  'Referral Marketing': `purple`,
  'Traditional Marketing': `sky`,
  Unknown: `gray`,
}

/** Series order for charts that pass no colorMap. */
export const DEFAULT_CHART_HUES: readonly ChartHue[] = [
  `blue`,
  `green`,
  `orange`,
  `red`,
  `sky`,
  `amber`,
  `purple`,
  `gray`,
]

/** Hue names get the chip recipe; anything else is a legacy solid color and renders unchanged. */
export function seriesStyle(color: string): SeriesStyle {
  return chartHueStyles[color as ChartHue] ?? { fill: color, fillHover: color, stroke: `none` }
}
